// Activo Fijo → Configuración → Mapeo contable (conceptos af_*).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { getErrorMessage } from '@/lib/api/client';
import { BuscadorCuenta } from '@/components/ui/BuscadorCuenta';
import { CuentaMap, afRpc } from './shared';

export function MapeoAF() {
  const qc = useQueryClient();
  const { data: rows = [], isFetching } = useQuery<CuentaMap[]>({ queryKey: ['af-cuentas'], queryFn: () => afRpc<CuentaMap[]>('af_cuentas_get') });

  const setCuenta = useMutation({
    mutationFn: (v: { concepto: string; cuenta_id: string | null }) => afRpc('af_cuenta_set', { _concepto: v.concepto, _cuenta_id: v.cuenta_id }),
    onSuccess: () => { toast.success('Mapeo actualizado'); void qc.invalidateQueries({ queryKey: ['af-cuentas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Lo que falta por asignar entre las obligatorias. Sin esto, el usuario sólo
  // se entera cuando la carga inicial o la corrida fallan — y fallan con un
  // aviso, no con un error, así que es fácil no enterarse nunca.
  const faltan = rows.filter((r) => r.requerido && !r.cuenta_id);

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Link2 className="w-5 h-5" /> Mapeo contable</h1>
      <p className="text-sm text-muted-foreground">Cuenta contable para cada concepto del módulo. Las marcadas como <b>obligatorias</b> se necesitan para cargar los activos que la empresa ya tiene y para cerrar la corrida mensual; el resto se usa en valorización, bajas y ventas.</p>

      {faltan.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-400 bg-amber-50 p-3 text-sm dark:border-amber-900/50 dark:bg-amber-900/15">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            <p className="font-medium text-amber-800 dark:text-amber-300">
              Falta{faltan.length > 1 ? 'n' : ''} {faltan.length} cuenta{faltan.length > 1 ? 's' : ''} obligatoria{faltan.length > 1 ? 's' : ''}: {faltan.map((r) => r.label).join(' · ')}
            </p>
            <p className="mt-0.5 text-amber-800/80 dark:text-amber-300/80">
              Hasta asignarlas, el alta de activos y la depreciación se registran pero <b>no generan asiento contable</b>.
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">Conceptos {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {rows.map((r) => {
            const falta = !!r.requerido && !r.cuenta_id;
            return (
              <div key={r.concepto} className="flex items-start gap-3 border-b last:border-0 py-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    {r.label}
                    {r.requerido && (
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${falta
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                        : 'bg-muted text-muted-foreground'}`}>Obligatoria</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">{r.concepto}{r.codigo ? ` · ${r.codigo} ${r.nombre}` : ''}</div>
                  {r.ayuda && <div className="mt-0.5 text-xs text-muted-foreground">{r.ayuda}</div>}
                </div>
                <div className="w-72 shrink-0">
                  <BuscadorCuenta value={r.cuenta_id ?? ''}
                    onChange={(v) => setCuenta.mutate({ concepto: r.concepto, cuenta_id: v || null })}
                    allowClear placeholder={falta ? '— Falta asignar —' : '— Sin asignar —'} />
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
